import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { GIRVI_THEME } from "./girviTheme";
import "./girvi.css";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import {
  listGirvis,
  showGirvi,
  settlementGirvi,
  listGirviBanks,
} from "../../erpClient";
import {
  createGirviIdempotencyKey,
  isGirviApiSuccess,
  normalizeListGirvisRows,
  getGirviShowFirstObject,
} from "../../utils/girviErpAdapter";
import { formatIndianRupee } from "../../utils/formatLiveRate";

/** ERP sometimes returns reference objects like `{ id, name }` — never render them raw in JSX. */
function safeDisplayValue(value, fallback = "") {
  if (value == null || value === "") return fallback;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (typeof value === "object") {
    if (value.name != null) return String(value.name);
    if (value.label != null) return String(value.label);
    if (value.id != null) return `#${value.id}`;
  }
  return fallback;
}

function mapShowGirviToPanelData(firstObj) {
  if (!firstObj || typeof firstObj !== "object") return null;
  const nested = firstObj.girvi_details || firstObj.detail || {};
  const detail = {
    ...nested,
    girvi_date:
      nested.girvi_date ?? firstObj.girvi_date ?? firstObj.doc_date ?? "",
    monthly_interest:
      nested.monthly_interest ?? firstObj.monthly_interest ?? "",
    interest_percent:
      nested.interest_percent ?? firstObj.interest_percent ?? "",
    remaining_amount:
      nested.remaining_amount ??
      firstObj.remaining_amount ??
      firstObj.remaining_amt ??
      "",
    principal_given:
      nested.principal_given ??
      firstObj.principal_given ??
      firstObj.principal_amount ??
      "",
  };
  const items = Array.isArray(firstObj.items) ? firstObj.items : [];
  const payments = Array.isArray(firstObj.payments) ? firstObj.payments : [];
  return { detail, items, payments };
}

function toDateOnly(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value).trim();
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayYMD() {
  return formatYMD(new Date());
}

function addMonthsAndDays(fromYmd, months, days) {
  const d = toDateOnly(fromYmd);
  if (!d) return "";
  d.setMonth(d.getMonth() + (parseInt(months, 10) || 0));
  d.setDate(d.getDate() + (parseInt(days, 10) || 0));
  return formatYMD(d);
}

/** Calendar diff: whole months + leftover days between From and To. */
function diffMonthsAndDays(fromYmd, toYmd) {
  const from = toDateOnly(fromYmd);
  const to = toDateOnly(toYmd);
  if (!from || !to || to < from) return { months: 0, leftoverDays: 0 };

  let months =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth());
  let fromPlusMonths = new Date(
    from.getFullYear(),
    from.getMonth() + months,
    from.getDate(),
  );
  if (fromPlusMonths > to) {
    months -= 1;
    fromPlusMonths = new Date(
      from.getFullYear(),
      from.getMonth() + months,
      from.getDate(),
    );
  }
  const leftoverDays = Math.round(
    (to - fromPlusMonths) / (1000 * 60 * 60 * 24),
  );
  return {
    months: Math.max(0, months),
    leftoverDays: Math.max(0, leftoverDays),
  };
}

/** Interest Days uses 30-day months: 2 months → 60 days. */
function toTotalInterestDays(months, leftoverDays = 0) {
  return (parseInt(months, 10) || 0) * 30 + (parseInt(leftoverDays, 10) || 0);
}

function fromTotalInterestDays(totalDays) {
  const d = Math.max(0, parseInt(totalDays, 10) || 0);
  return {
    months: Math.floor(d / 30),
    leftoverDays: d % 30,
  };
}

function isAdvancedInterestPayment(p) {
  const remarks = String(p?.remarks || "").toLowerCase();
  return (
    remarks.includes("advanced interest") ||
    remarks.includes("advance interest")
  );
}

function getPaymentDateYmd(p) {
  const raw = p?.payment_date || p?.date || p?.created_at;
  const d = toDateOnly(raw);
  return d ? formatYMD(d) : "";
}

/**
 * First payment: From = issue date.
 * Later payments: From = last settlement/installment payment date.
 */
function resolvePeriodFromDate(detail, payments) {
  const issueDate = toDateOnly(detail?.girvi_date);
  const issueYmd = issueDate ? formatYMD(issueDate) : todayYMD();
  const relevant = (payments || []).filter((p) => !isAdvancedInterestPayment(p));
  if (relevant.length === 0) return issueYmd;

  let latest = null;
  for (const p of relevant) {
    const ymd = getPaymentDateYmd(p);
    if (!ymd) continue;
    if (!latest || ymd > latest) latest = ymd;
  }
  return latest || issueYmd;
}

function getMonthlyInterestAmount(detail, listFallback) {
  const monthly = parseFloat(
    detail?.monthly_interest ?? listFallback?.monthly_interest,
  );
  if (Number.isFinite(monthly) && monthly > 0) return monthly;

  const remaining =
    parseFloat(
      detail?.remaining_amount ??
        listFallback?.remaining_amount ??
        detail?.principal_given ??
        listFallback?.principal_given ??
        0,
    ) || 0;
  const pct =
    parseFloat(
      detail?.interest_percent ?? listFallback?.interest_percent ?? 0,
    ) || 0;
  return (remaining * pct) / 100;
}

/** Interest = (monthly / 30) × total interest days (30-day month). */
function calculatePeriodInterest(monthlyAmt, totalInterestDays) {
  const monthly = parseFloat(monthlyAmt) || 0;
  const days = parseFloat(totalInterestDays) || 0;
  return (monthly / 30) * days;
}

const emptyPeriodState = () => ({
  fromDate: "",
  toDate: "",
  intMonth: "",
  interestDays: "",
});

function GirviQuickPayment() {
  const navigate = useNavigate();
  const [interestReceived, setInterestReceived] = useState("");
  const [principalReceived, setPrincipalReceived] = useState("");
  const [remarks, setRemarks] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayYMD());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [intMonth, setIntMonth] = useState("");
  const [interestDays, setInterestDays] = useState("");
  const [payMode, setPayMode] = useState("Cash");
  const [bankType, setBankType] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [girviOptions, setGirviOptions] = useState([]);
  const [selectedGirvi, setSelectedGirvi] = useState(null);

  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();

  const canQuickPayment = hasPermission(PERMISSIONS.GIRVIQUICKPAYMENT);
  const settlementIdempotencyKeyRef = useRef(null);
  const settlementPayloadFingerprintRef = useRef(null);
  /** Prevents circular updates between months/days and dates. */
  const periodSyncLockRef = useRef(false);

  const monthlyInterestAmt = getMonthlyInterestAmount(
    selectedGirvi?.data?.detail,
    selectedGirvi?.listData,
  );
  const interestPercent = parseFloat(
    selectedGirvi?.data?.detail?.interest_percent ??
      selectedGirvi?.listData?.interest_percent ??
      0,
  );
  const calculatedInterest = calculatePeriodInterest(
    monthlyInterestAmt,
    interestDays,
  );

  const applyCalculatedInterest = (totalDays, detail, listFallback) => {
    const monthly = getMonthlyInterestAmount(detail, listFallback);
    const amt = calculatePeriodInterest(monthly, totalDays);
    setInterestReceived(amt > 0 ? amt.toFixed(2) : "");
  };

  const initPeriodForGirvi = (panelData, listFallback) => {
    const detail = panelData?.detail || {};
    const payments = panelData?.payments || [];
    const from = resolvePeriodFromDate(detail, payments);
    const to = todayYMD();
    const { months, leftoverDays } = diffMonthsAndDays(from, to);
    const totalDays = toTotalInterestDays(months, leftoverDays);

    periodSyncLockRef.current = true;
    setFromDate(from);
    setToDate(to);
    setIntMonth(String(months));
    setInterestDays(String(totalDays));
    setPaymentDate(to);
    applyCalculatedInterest(totalDays, detail, listFallback);
    periodSyncLockRef.current = false;
  };

  const resetPeriodFields = () => {
    const empty = emptyPeriodState();
    setFromDate(empty.fromDate);
    setToDate(empty.toDate);
    setIntMonth(empty.intMonth);
    setInterestDays(empty.interestDays);
  };

  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (periodSyncLockRef.current || !value || !toDate) return;
    periodSyncLockRef.current = true;
    const { months, leftoverDays } = diffMonthsAndDays(value, toDate);
    const totalDays = toTotalInterestDays(months, leftoverDays);
    setIntMonth(String(months));
    setInterestDays(String(totalDays));
    applyCalculatedInterest(
      totalDays,
      selectedGirvi?.data?.detail,
      selectedGirvi?.listData,
    );
    periodSyncLockRef.current = false;
  };

  const handleToDateChange = (value) => {
    setToDate(value);
    if (value) setPaymentDate(value);
    if (periodSyncLockRef.current || !value || !fromDate) return;
    periodSyncLockRef.current = true;
    const { months, leftoverDays } = diffMonthsAndDays(fromDate, value);
    const totalDays = toTotalInterestDays(months, leftoverDays);
    setIntMonth(String(months));
    setInterestDays(String(totalDays));
    applyCalculatedInterest(
      totalDays,
      selectedGirvi?.data?.detail,
      selectedGirvi?.listData,
    );
    periodSyncLockRef.current = false;
  };

  const handleIntMonthChange = (value) => {
    const cleaned =
      value === "" ? "" : String(Math.max(0, parseInt(value, 10) || 0));
    setIntMonth(cleaned);
    if (periodSyncLockRef.current || !fromDate) return;
    periodSyncLockRef.current = true;
    const months = cleaned === "" ? 0 : parseInt(cleaned, 10) || 0;
    const totalDays = toTotalInterestDays(months, 0);
    setInterestDays(String(totalDays));
    const nextTo = addMonthsAndDays(fromDate, months, 0);
    setToDate(nextTo);
    if (nextTo) setPaymentDate(nextTo);
    applyCalculatedInterest(
      totalDays,
      selectedGirvi?.data?.detail,
      selectedGirvi?.listData,
    );
    periodSyncLockRef.current = false;
  };

  const handleInterestDaysChange = (value) => {
    const cleaned =
      value === "" ? "" : String(Math.max(0, parseInt(value, 10) || 0));
    setInterestDays(cleaned);
    if (periodSyncLockRef.current || !fromDate) return;
    periodSyncLockRef.current = true;
    const totalDays = cleaned === "" ? 0 : parseInt(cleaned, 10) || 0;
    const { months, leftoverDays } = fromTotalInterestDays(totalDays);
    setIntMonth(String(months));
    const nextTo = addMonthsAndDays(fromDate, months, leftoverDays);
    setToDate(nextTo);
    if (nextTo) setPaymentDate(nextTo);
    applyCalculatedInterest(
      totalDays,
      selectedGirvi?.data?.detail,
      selectedGirvi?.listData,
    );
    periodSyncLockRef.current = false;
  };

  const payModeOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Cheque", label: "Cheque" },
    { value: "Bank Transfer", label: "Bank Transfer" },
    { value: "UPI", label: "UPI" },
    { value: "Card", label: "Card" },
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: "8px",
      borderColor: GIRVI_THEME.borderColor,
      padding: "2px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10050,
    }),
    option: (base, state) => {
      const focused = Boolean(state.isFocused);
      const selected = Boolean(state.isSelected);
      let backgroundColor = "#ffffff";
      let color = "var(--bs-heading-color, #212529)";
      if (selected) {
        backgroundColor = "var(--theme-primary, #962a29)";
        color = "#ffffff";
      } else if (focused) {
        backgroundColor = "#DEEBFF";
        color = "#212529";
      }
      return {
        ...base,
        backgroundColor,
        color,
        fontWeight: selected ? 600 : 400,
        cursor: "pointer",
        ":active": {
          backgroundColor: selected
            ? "var(--theme-primary, #962a29)"
            : "#B2D4FF",
          color: selected ? "#ffffff" : "#212529",
        },
      };
    },
  };

  const selectMenuProps = {
    classNamePrefix: "girvi-select",
    styles: selectStyles,
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    onKeyDown: (event) => event.stopPropagation(),
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in GirviQuickPayment, refetching data...",
        event.detail,
      );
      fetchBanks();
      setGirviOptions([]);
      setSelectedGirvi(null);
      setInterestReceived("");
      setPrincipalReceived("");
      resetPeriodFields();
      setPaymentDate(todayYMD());
      setRemarks("");
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  useEffect(() => {
    if (payMode === "Cash" && paymentTypes.length > 0 && !bankType) {
      const cashBank = paymentTypes.find((b) =>
        b.label.toLowerCase().includes("cash"),
      );
      if (cashBank) {
        setBankType(cashBank.value);
      }
    }
  }, [paymentTypes, payMode, bankType]);

  const fetchGirviList = async (search = "") => {
    const trimmed = String(search || "").trim();
    if (trimmed.length < 3) {
      setGirviOptions([]);
      return [];
    }

    try {
      const response = await listGirvis({
        search: trimmed,
        page: 1,
        limit: 10,
      });

      if (isGirviApiSuccess(response)) {
        const rows = normalizeListGirvisRows(response);
        const options = rows.map((item) => ({
          value: String(item.id ?? item.girvi_document_id ?? item.girvi_id),
          label: `${item.girvi_number || "N/A"} - ${item.customer_name || "Unknown"} [${item.remaining_amount || 0
            } / ${item.principal_given || 0}]`,
          data: item,
        }));
        setGirviOptions(options);
        return options;
      }
      setGirviOptions([]);
      return [];
    } catch (error) {
      console.error("Error fetching girvi list:", error);
      setGirviOptions([]);
      return [];
    }
  };

  const handleGirviSearchInput = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (trimmed.length < 3) {
      setGirviOptions([]);
      return;
    }
    void fetchGirviList(trimmed);
  };

  const handleGirviSelect = async (option) => {
    setInterestReceived("");
    setPrincipalReceived("");
    resetPeriodFields();
    if (!option) {
      setSelectedGirvi(null);
      return;
    }

    setSelectedGirvi({ ...option, data: null, listData: option.data });

    try {
      const res = await showGirvi({ id: Number(option.value) });
      if (!isGirviApiSuccess(res)) {
        setSelectedGirvi({ ...option, listData: option.data });
        const fallbackDetail = {
          girvi_date: option.data?.girvi_date,
          monthly_interest: option.data?.monthly_interest,
          interest_percent: option.data?.interest_percent,
          remaining_amount: option.data?.remaining_amount,
          principal_given: option.data?.principal_given,
        };
        initPeriodForGirvi(
          { detail: fallbackDetail, payments: [], items: [] },
          option.data,
        );
        return;
      }
      const raw = getGirviShowFirstObject(res);
      const panelData = mapShowGirviToPanelData(raw);
      const nextData =
        panelData || {
          detail: {},
          items: [],
          payments: [],
        };
      setSelectedGirvi({
        ...option,
        data: nextData,
        listData: option.data,
      });
      initPeriodForGirvi(nextData, option.data);
    } catch (error) {
      console.error("GirviQuickPayment: showGirvi failed:", error);
      setSelectedGirvi({ ...option, listData: option.data });
      initPeriodForGirvi(
        {
          detail: {
            girvi_date: option.data?.girvi_date,
            monthly_interest: option.data?.monthly_interest,
            interest_percent: option.data?.interest_percent,
            remaining_amount: option.data?.remaining_amount,
            principal_given: option.data?.principal_given,
          },
          payments: [],
          items: [],
        },
        option.data,
      );
    }
  };

  const handleClearUser = () => {
    setSelectedGirvi(null);
    setInterestReceived("");
    setPrincipalReceived("");
    setRemarks("");
    resetPeriodFields();
    setPaymentDate(todayYMD());
  };

  const fetchBanks = async () => {
    try {
      const response = await listGirviBanks({});
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.girvi_banks)
          ? response.data.girvi_banks
          : [];

      if (isGirviApiSuccess(response)) {
        setPaymentTypes(
          rows.map((bank) => ({
            value: String(bank.id),
            label:
              bank.bank_name || bank.name || bank.account_type || `Bank ${bank.id}`,
          })),
        );
      } else {
        setPaymentTypes([]);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      setPaymentTypes([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canQuickPayment) {
      toast.error("Not authorized for quick payment");
      return;
    }

    // If user did not explicitly select, we fallback to first option.
    // In that case, we may need to fetch full document details via `show`.
    const isExplicitSelection = Boolean(selectedGirvi);
    const effectiveGirvi = selectedGirvi ?? (girviOptions[0] || null);
    if (!effectiveGirvi) {
      toast.error("Please select a Dihran first");
      return;
    }

    const intAmt = parseFloat(interestReceived) || 0;
    const prinAmt = parseFloat(principalReceived) || 0;
    const totalAmt = intAmt + prinAmt;

    if (totalAmt <= 0) {
      toast.error("Please enter Interest or Principal amount");
      return;
    }

    if (payMode !== "Cash" && !bankType) {
      toast.error("Please select a bank");
      return;
    }

    try {
      const girviId = Number(effectiveGirvi.value);
      const bankId = bankType ? parseInt(bankType, 10) : 0;
      const payModeMap = {
        Cash: "CASH",
        Cheque: "CHEQUE",
        "Bank Transfer": "BANK",
        UPI: "UPI",
        Card: "CARD",
      };
      const payModeValue =
        payModeMap[payMode] ||
        String(payMode || "")
          .toUpperCase()
          .replace(/\s+/g, "_");

      const payment = {
        principal_received: prinAmt,
        interest_received: intAmt,
        bank_id: bankId,
        pay_mode: payModeValue,
        payment_date: paymentDate,
        from_date: fromDate || null,
        to_date: toDate || null,
        int_month: intMonth === "" ? 0 : parseInt(intMonth, 10) || 0,
        interest_days:
          interestDays === "" ? 0 : parseInt(interestDays, 10) || 0,
        remarks: remarks || "",
      };

      const fingerprint = JSON.stringify({ girvi_id: girviId, payment });
      if (
        !settlementIdempotencyKeyRef.current ||
        settlementPayloadFingerprintRef.current !== fingerprint
      ) {
        settlementIdempotencyKeyRef.current = createGirviIdempotencyKey();
        settlementPayloadFingerprintRef.current = fingerprint;
      }

      const response = await settlementGirvi({
        idempotency_key: settlementIdempotencyKeyRef.current,
        girvi_id: girviId,
        payment,
      });

      if (isGirviApiSuccess(response)) {
        toast.success("Payment recorded successfully!");

        // Refresh by calling show() only when we used fallback (no explicit selection).
        // When user selected from dropdown, we already have enough list data for UI,
        // so avoid extra backend request.
        if (!isExplicitSelection) {
          try {
            await showGirvi({ id: effectiveGirvi.value });
          } catch {
            // ignore
          }
        }

        settlementIdempotencyKeyRef.current = null;
        settlementPayloadFingerprintRef.current = null;

        // Reset all fields and clear selection
        handleClearUser();
      } else {
        toast.error(response?.message || "Failed to record payment");
      }
    } catch (error) {
      console.error("Payment Error", error);
      toast.error(error.response?.data?.message || "Failed to record payment");
    }
  };

  return (
    <>
      <style>
        {`
          .girvi-customer-dropdown .dropdown-item:hover,
          .girvi-customer-dropdown .dropdown-item.active {
            background-color: ${GIRVI_THEME.bgColor} !important;
            color: ${GIRVI_THEME.textColor} !important;
          }
          .form-control::placeholder {
            font-size: 0.85rem;
          }
        `}
      </style>
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
          >
            {/* Header Section */}
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Dihran Payment</h4>
            </div>

            {/* Body Section */}
            <div className="card-body p-4">
              <div className="row">
                {/* Left Side - Form */}
                <div className="col-lg-5 border-end">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      {/* Search Dihran */}
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Select User
                        </label>
                        <div className="position-relative">
                          <Select
                            options={girviOptions}
                            value={selectedGirvi}
                            onChange={handleGirviSelect}
                            onInputChange={(inputValue, meta) => {
                              if (meta.action === "input-change") {
                                handleGirviSearchInput(inputValue);
                              }
                              return inputValue;
                            }}
                            placeholder="Search mobile / name / Dihran no..."
                            isSearchable
                            isClearable={false}
                            filterOption={() => true}
                            noOptionsMessage={({ inputValue }) =>
                              String(inputValue || "").trim().length < 3
                                ? "Type at least 3 characters to search"
                                : "No Dihran found"
                            }
                            {...selectMenuProps}
                          />
                          {selectedGirvi && (
                            <button
                              type="button"
                              className="btn btn-sm position-absolute"
                              style={{
                                top: "50%",
                                right: "40px",
                                transform: "translateY(-50%)",
                                background: "none",
                                border: "none",
                                color: "#999",
                                fontSize: "18px",
                                lineHeight: "1",
                                padding: "4px",
                                zIndex: 10,
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                              onClick={handleClearUser}
                              title="Clear selection"
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#f0f0f0";
                                e.target.style.color = "#333";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "none";
                                e.target.style.color = "#999";
                              }}
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Interest period: From / To ↔ Int Month / Interest Days */}
                      <div className="col-6">
                        <label className="form-label fw-semibold">From</label>
                        <input
                          type="date"
                          className="form-control"
                          value={fromDate}
                          onChange={(e) => handleFromDateChange(e.target.value)}
                          disabled={!selectedGirvi}
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold">
                          Int Month
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={intMonth}
                          onChange={(e) => handleIntMonthChange(e.target.value)}
                          disabled={!selectedGirvi}
                          min="0"
                          step="1"
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                            backgroundColor: selectedGirvi
                              ? "rgba(255, 102, 146, 0.08)"
                              : undefined,
                          }}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold">
                          To Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={toDate}
                          onChange={(e) => handleToDateChange(e.target.value)}
                          disabled={!selectedGirvi}
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold">
                          Interest Days
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={interestDays}
                          onChange={(e) =>
                            handleInterestDaysChange(e.target.value)
                          }
                          disabled={!selectedGirvi}
                          min="0"
                          step="1"
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                      </div>

                      {/* Interest rate / month */}
                      <div className="col-12">
                        <div
                          className="d-flex flex-wrap gap-3 align-items-center px-2 py-2 rounded"
                          style={{
                            backgroundColor: "rgba(3, 62, 62, 0.06)",
                            border: `1px solid ${GIRVI_THEME.borderColor}`,
                          }}
                        >
                          <span className="small mb-0">
                            <span className="text-muted">Interest rate / month: </span>
                            <strong>
                              {Number.isFinite(interestPercent) && interestPercent > 0
                                ? `${interestPercent}%`
                                : "—"}
                            </strong>
                          </span>
                          <span className="small mb-0">
                            <span className="text-muted">Monthly interest: </span>
                            <strong>
                              {monthlyInterestAmt > 0
                                ? formatIndianRupee(monthlyInterestAmt)
                                : "₹0"}
                            </strong>
                          </span>
                          <span className="small mb-0">
                            <span className="text-muted">Period interest: </span>
                            <strong style={{ color: GIRVI_THEME.bgColor }}>
                              {calculatedInterest > 0
                                ? formatIndianRupee(calculatedInterest)
                                : "₹0"}
                            </strong>
                          </span>
                        </div>
                      </div>

                      {/* Principal Received */}
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Principal Received
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={principalReceived}
                          onChange={(e) => setPrincipalReceived(e.target.value)}
                          placeholder=""
                          step="0.01"
                          min="0"
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                      </div>

                      {/* Interest Received */}
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Interest Received
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={interestReceived}
                          onChange={(e) => setInterestReceived(e.target.value)}
                          placeholder={
                            calculatedInterest > 0
                              ? calculatedInterest.toFixed(2)
                              : safeDisplayValue(
                                  selectedGirvi?.data?.detail?.monthly_interest,
                                  "",
                                )
                          }
                          step="0.01"
                          min="0"
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                        {selectedGirvi && (
                          <small className="text-muted">
                            Auto-filled from period (
                            {intMonth || 0} month
                            {String(intMonth) === "1" ? "" : "s"}
                            {interestDays !== "" && interestDays != null
                              ? ` / ${interestDays} day${String(interestDays) === "1" ? "" : "s"}`
                              : ""}
                            ). You can edit if needed.
                          </small>
                        )}
                      </div>

                      {/* Payment Mode and Bank */}
                      <div className="col-6">
                        <label className="form-label fw-semibold">
                          Payment Mode
                        </label>
                        <Select
                          options={payModeOptions}
                          value={payModeOptions.find(
                            (opt) => opt.value === payMode,
                          )}
                          onChange={(selectedOption) => {
                            const mode = selectedOption
                              ? selectedOption.value
                              : "";
                            setPayMode(mode);
                            if (mode === "Cash") {
                              const cashBank = paymentTypes.find((b) =>
                                b.label.toLowerCase().includes("cash"),
                              );
                              if (cashBank) {
                                setBankType(cashBank.value);
                              }
                            }
                          }}
                          {...selectMenuProps}
                        />
                      </div>

                      {/* Bank Selection */}
                      <div className="col-6">
                        <label className="form-label fw-semibold">Bank</label>
                        <Select
                          options={paymentTypes}
                          value={paymentTypes.find(
                            (opt) => opt.value === bankType,
                          )}
                          onChange={(selectedOption) =>
                            setBankType(
                              selectedOption ? selectedOption.value : "",
                            )
                          }
                          {...selectMenuProps}
                          placeholder="Select Bank"
                        />
                      </div>

                      {/* Payment Date */}
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Payment Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={paymentDate}
                          onChange={(e) => setPaymentDate(e.target.value)}
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                      </div>

                      {/* Remark */}
                      <div className="col-12">
                        <label className="form-label fw-semibold">Remark</label>
                        <input
                          type="text"
                          className="form-control"
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Enter remarks..."
                          style={{
                            borderRadius: "8px",
                            borderColor: GIRVI_THEME.borderColor,
                            padding: "10px",
                          }}
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="col-12 text-end mt-4">
                        <button
                          type="submit"
                          className="btn btn-primary px-4 py-2"
                          style={{
                            borderRadius: "6px",
                            border: "none",
                          }}
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Right Side - Information */}
                <div className="col-lg-7">
                  <div className="h-100 p-3">
                    {selectedGirvi ? (
                      <>
                        <h5 className="fw-bold text-dark mb-3">Information</h5>
                        <div className="mb-3">
                          <span className="fw-bold mb-2">Date:</span>{" "}
                          <span className="fw-semibold">
                            {safeDisplayValue(
                              selectedGirvi?.data?.detail?.girvi_date,
                              "N/A",
                            )}
                          </span>
                        </div>
                        <div className="mb-3">
                          <span className="fw-bold mb-2">Interest / month:</span>{" "}
                          <span className="fw-semibold">
                            {Number.isFinite(interestPercent) &&
                            interestPercent > 0
                              ? `${interestPercent}%`
                              : "N/A"}
                            {" · "}{monthlyInterestAmt > 0
                              ? formatIndianRupee(monthlyInterestAmt)
                              : "₹0"}
                          </span>
                        </div>
                        <h6 className="fw-bold mb-2">Item Details</h6>
                        <div className="table-responsive mb-3">
                          <table className="table table-sm table-bordered bg-white">
                            <thead>
                              <tr>
                                <th>Product Group</th>
                                <th>GWT</th>
                                <th>LWT</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(selectedGirvi?.data?.items || []).map(
                                (it, idx) => (
                                <tr
                                  key={
                                    it.id ??
                                    it.girvi_item_id ??
                                    `item-${idx}`
                                  }
                                >
                                  <td>
                                    {safeDisplayValue(
                                      it.product_group_name ?? it.product_group,
                                      "N/A",
                                    )}
                                  </td>
                                  <td>{safeDisplayValue(it.gwt, "0.00")}</td>
                                  <td>{safeDisplayValue(it.lwt, "0.00")}</td>
                                  <td>
                                    {(() => {
                                      const t = it.totalamt;
                                      if (t == null || t === "")
                                        return "₹0";
                                      return formatIndianRupee(t);
                                    })()}
                                  </td>
                                </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                        <h6 className="fw-bold mb-2">Payment History</h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered bg-white">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Mode</th>
                                <th>Type(Bank)</th>
                                <th>Amount</th>
                                <th>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(selectedGirvi?.data?.payments || []).map(
                                (p, pIdx) => {
                                  const rawBankRef =
                                    p.bank_id ??
                                    (p.bank_type &&
                                    typeof p.bank_type === "object"
                                      ? p.bank_type.id
                                      : p.bank_type);
                                  const bankIdNum = parseInt(
                                    String(rawBankRef ?? -1),
                                    10,
                                  );
                                  const bankLabel =
                                    (typeof p.bank_name === "string" &&
                                    p.bank_name.trim()
                                      ? p.bank_name.trim()
                                      : null) ||
                                    paymentTypes.find(
                                      (b) =>
                                        parseInt(b.value, 10) === bankIdNum,
                                    )?.label ||
                                    (Number.isFinite(bankIdNum) && bankIdNum > 0
                                      ? `Bank ${bankIdNum}`
                                      : "N/A");
                                  const rawDate =
                                    p.date || p.payment_date || p.created_at;
                                  const dateStr =
                                    typeof rawDate === "string"
                                      ? rawDate.split("T")[0]
                                      : "N/A";
                                  const principalPaid =
                                    parseFloat(p.principal_paid) || 0;
                                  const interestPaid =
                                    parseFloat(p.interest_paid) || 0;
                                  const amountTotal =
                                    p.amount != null && p.amount !== ""
                                      ? parseFloat(p.amount) || 0
                                      : principalPaid + interestPaid;
                                  return (
                                    <tr key={p.id ?? `pay-${pIdx}`}>
                                      <td>{dateStr}</td>
                                      <td>
                                        {safeDisplayValue(p.pay_mode, "N/A")}
                                      </td>
                                      <td>{bankLabel}</td>
                                      <td>
                                        {formatIndianRupee(amountTotal)}
                                      </td>
                                      <td>
                                        {safeDisplayValue(p.remarks, "-")}
                                      </td>
                                    </tr>
                                  );
                                },
                              )}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div
                        className="p-4 rounded text-center"
                        style={{
                          height: "calc(100% - 40px)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <p className="mb-0">
                          Select a transaction to view details
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default GirviQuickPayment;
